import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, BookOpen, Trash2, Edit2, X, Image } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DiaryPage() {
  const [searchParams] = useSearchParams();
  const defaultProject = searchParams.get('project') || '';

  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState(defaultProject);
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ project_id: defaultProject, plant_id: '', title: '', content: '', images: [] });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const url = filterProject ? `${API}/api/diary?project_id=${filterProject}` : `${API}/api/diary`;
      const [dRes, prRes, plRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/api/projects`),
        axios.get(`${API}/api/plants`),
      ]);
      setEntries(Array.isArray(dRes.data) ? dRes.data : []);
      setProjects(Array.isArray(prRes.data) ? prRes.data : []);
      setPlants(Array.isArray(plRes.data) ? plRes.data : []);
    } catch { setEntries([]); setProjects([]); setPlants([]); }
    setLoading(false);
  }, [filterProject]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditEntry(null);
    setForm({ project_id: filterProject || (projects[0]?.id || ''), plant_id: '', title: '', content: '', images: [] });
    setError('');
    setShowModal(true);
  };

  const openEdit = (entry) => {
    setEditEntry(entry);
    setForm({ project_id: entry.project_id, plant_id: entry.plant_id || '', title: entry.title, content: entry.content, images: entry.images || [] });
    setError('');
    setShowModal(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, images: [...f.images, ev.target.result] }));
    reader.readAsDataURL(file);
  };

  const removeImage = (idx) => setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) { setError('Titel und Inhalt erforderlich'); return; }
    if (!form.project_id) { setError('Projekt auswählen'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, plant_id: form.plant_id || null };
      if (editEntry) {
        const { data } = await axios.put(`${API}/api/diary/${editEntry.id}`, payload);
        setEntries(prev => prev.map(e => e.id === data.id ? data : e));
      } else {
        const { data } = await axios.post(`${API}/api/diary`, payload);
        setEntries(prev => [data, ...prev]);
      }
      setShowModal(false);
    } catch (err) { setError(err.response?.data?.detail || 'Fehler'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eintrag löschen?')) return;
    try {
      await axios.delete(`${API}/api/diary/${id}`);
      setEntries(prev => prev.filter(e => e.id !== id));
    } catch {}
  };

  const getProjectName = (id) => projects.find(p => p.id === id)?.name || '';

  return (
    <div className="p-4 md:p-6 space-y-4 animate-fade-in" data-testid="diary-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-2xl font-bold text-emerald-50">Grow-Tagebuch</h1>
          <p className="text-emerald-100/50 text-sm mt-0.5">{entries.length} Einträge</p>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="create-diary-btn">
          <Plus size={16} /> Eintrag
        </button>
      </div>

      <select className="input-grow w-auto text-sm" value={filterProject} onChange={e => setFilterProject(e.target.value)}>
        <option value="">Alle Projekte</option>
        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : entries.length === 0 ? (
        <div className="text-center py-16" data-testid="no-diary">
          <BookOpen size={48} className="mx-auto text-emerald-500/30 mb-4" />
          <p className="text-emerald-100/50 mb-4">Noch keine Tagebucheinträge</p>
          <button onClick={openCreate} className="btn-primary">Ersten Eintrag erstellen</button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="diary-list">
          {entries.map(entry => (
            <div key={entry.id} className="card-grow overflow-hidden" data-testid={`diary-entry-${entry.id}`}>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-semibold text-emerald-100 truncate">{entry.title}</h3>
                      {entry.images?.length > 0 && (
                        <span className="badge badge-blue"><Image size={10} /> {entry.images.length}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-emerald-100/40">
                      <span>{new Date(entry.created_at).toLocaleDateString('de-DE')}</span>
                      {entry.project_id && <span>· {getProjectName(entry.project_id)}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => openEdit(entry)} className="p-1.5 rounded text-emerald-100/40 hover:text-emerald-400 transition-colors">
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => handleDelete(entry.id)} className="p-1.5 rounded text-emerald-100/40 hover:text-red-400 transition-colors" data-testid={`delete-diary-${entry.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className={`mt-2 text-sm text-emerald-100/60 ${expandedId === entry.id ? '' : 'line-clamp-3'}`}>
                  {entry.content}
                </div>
                {entry.content.length > 200 && (
                  <button onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                    className="text-xs text-emerald-400 hover:text-emerald-300 mt-1 transition-colors">
                    {expandedId === entry.id ? 'Weniger' : 'Mehr lesen'}
                  </button>
                )}
                {entry.images?.length > 0 && expandedId === entry.id && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {entry.images.map((img, i) => (
                      <img key={i} src={img} alt={`Foto ${i + 1}`} className="w-20 h-20 object-cover rounded-lg" />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-backdrop" data-testid="diary-modal">
          <div className="modal-content p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-emerald-50">{editEntry ? 'Eintrag bearbeiten' : 'Neuer Tagebucheintrag'}</h2>
              <button onClick={() => setShowModal(false)} className="text-emerald-100/40 hover:text-emerald-100"><X size={20} /></button>
            </div>
            <form onSubmit={handleSave} className="space-y-3">
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Projekt *</label>
                <select className="input-grow" value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))} data-testid="diary-project-select">
                  <option value="">Wählen...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Pflanze (optional)</label>
                <select className="input-grow" value={form.plant_id} onChange={e => setForm(f => ({ ...f, plant_id: e.target.value }))}>
                  <option value="">Alle / Keine</option>
                  {plants.filter(p => !form.project_id || p.project_id === form.project_id).map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Titel *</label>
                <input className="input-grow" placeholder="Titel des Eintrags" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="diary-title-input" />
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Inhalt *</label>
                <textarea className="input-grow" rows={5} placeholder="Was hat sich getan? Beobachtungen, Notizen..." value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} data-testid="diary-content-input" />
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Bilder</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <label style={{ cursor: 'pointer', fontSize: 13, color: '#4ade80' }}>
                    📷 Foto aufnehmen
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      style={{ display: 'none' }}
                      onChange={handleImageUpload}
                    />
                  </label>
                  <label style={{ cursor: 'pointer', fontSize: 13, color: '#60a5fa' }}>
                    🖼️ Aus Galerie
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleImageUpload}
                    />
                  </label>
                </div>
                {form.images.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {form.images.map((img, i) => (
                      <div key={i} className="relative">
                        <img src={img} alt="" className="w-16 h-16 object-cover rounded-lg" />
                        <button type="button" onClick={() => removeImage(i)}
                          className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-white"
                          style={{ background: '#EF4444' }}>
                          <X size={8} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Abbrechen</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="save-diary-btn">
                  {saving ? 'Speichern...' : editEntry ? 'Aktualisieren' : 'Eintragen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
